import { useState } from 'react';
import { defineMaterial, type MaterialComponentProps } from '@h5/schema';
import { FormFieldSchema, FormFieldTypeSchema, FormPropsSchema, type FormField, type FormFieldType, type FormProps } from './schema';

export { FormFieldSchema, FormFieldTypeSchema, FormPropsSchema, type FormField, type FormFieldType, type FormProps };

type FormValue = string | string[];
type Values = Record<string, FormValue>;

const PHONE_RE = /^1[3-9]\d{9}$/;

/** 前端校验：必填 + 手机号格式。返回 fieldId → 错误文案。 */
export function validateForm(fields: FormField[], values: Values): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const v = values[f.id];
    const empty = v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
    if (f.required && empty) {
      errors[f.id] = `${f.label || '该项'}为必填`;
      continue;
    }
    if (f.type === 'phone' && !empty && typeof v === 'string' && !PHONE_RE.test(v.trim())) {
      errors[f.id] = '手机号格式不正确';
    }
  }
  return errors;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid #d9d9d9',
  borderRadius: 4,
  background: '#fff',
  color: '#333',
  outline: 'none',
};

function FieldControl({ field, value, onChange, disabled }: {
  field: FormField; value: FormValue | undefined; onChange: (v: FormValue) => void; disabled: boolean;
}) {
  const str = typeof value === 'string' ? value : '';
  const arr = Array.isArray(value) ? value : [];
  switch (field.type) {
    case 'textarea':
      return <textarea style={{ ...inputStyle, minHeight: 72, resize: 'none' }} placeholder={field.placeholder} value={str} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    case 'select':
      return (
        <select style={inputStyle} value={str} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          <option value="">{field.placeholder || '请选择'}</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case 'radio':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 14 }}>
          {field.options.map((o) => (
            <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="radio" name={field.id} value={o} checked={str === o} disabled={disabled} onChange={() => onChange(o)} />{o}
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 14 }}>
          {field.options.map((o) => (
            <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox" value={o} checked={arr.includes(o)} disabled={disabled}
                onChange={(e) => onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))}
              />{o}
            </label>
          ))}
        </div>
      );
    case 'phone':
      return <input style={inputStyle} type="tel" inputMode="numeric" maxLength={11} placeholder={field.placeholder} value={str} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    case 'input':
    default:
      return <input style={inputStyle} type="text" placeholder={field.placeholder} value={str} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  }
}

/** 表单物料：容器物料。编辑态可交互但不提交；预览态调用 ctx.api.submitForm(layer.id, data)。 */
function FormView({ props, ctx }: MaterialComponentProps<FormProps>) {
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const isEdit = ctx.mode === 'edit';

  const submit = async () => {
    if (isEdit || status === 'submitting') return;
    const errs = validateForm(props.fields, values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      await ctx.api.submitForm(ctx.layer.id, values);
      setStatus('success');
    } catch (e) {
      setStatus('error');
      setErrorMsg(e instanceof Error ? e.message : '提交失败，请稍后重试');
    }
  };

  if (status === 'success') {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: props.submitColor, boxSizing: 'border-box' }}>
        {props.successText}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); void submit(); }}
      style={{ width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto', padding: 4 }}
    >
      {props.fields.map((f) => (
        <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {f.label ? (
            <label style={{ fontSize: 13, color: '#333' }}>
              {f.required ? <span style={{ color: '#ff4d4f', marginRight: 2 }}>*</span> : null}{f.label}
            </label>
          ) : null}
          <FieldControl
            field={f}
            value={values[f.id]}
            disabled={status === 'submitting'}
            onChange={(v) => {
              setValues((prev) => ({ ...prev, [f.id]: v }));
              if (errors[f.id]) setErrors((prev) => { const n = { ...prev }; delete n[f.id]; return n; });
            }}
          />
          {errors[f.id] ? <span style={{ fontSize: 12, color: '#ff4d4f' }}>{errors[f.id]}</span> : null}
        </div>
      ))}
      {status === 'error' && errorMsg ? <div style={{ fontSize: 12, color: '#ff4d4f' }}>{errorMsg}</div> : null}
      <button
        type="submit"
        disabled={status === 'submitting'}
        style={{
          marginTop: 4, padding: '10px 0', border: 'none', borderRadius: 6, fontSize: 15,
          background: props.submitColor, color: '#fff', cursor: isEdit ? 'default' : 'pointer',
          opacity: status === 'submitting' ? 0.7 : 1,
        }}
      >
        {status === 'submitting' ? '提交中…' : props.submitText}
      </button>
    </form>
  );
}

export const formMaterial = defineMaterial<FormProps>({
  id: 'form',
  version: 1,
  name: '表单',
  category: 'interactive',
  icon: '☰',
  defaultSize: { w: 335, h: 260 },
  defaultProps: FormPropsSchema.parse({}),
  propsSchema: FormPropsSchema,
  capabilities: { container: true, rotatable: false },
  component: FormView,
});
